/**
 * Small inline icon set, drawn from Lucide-style geometry.
 *
 * All icons:
 *   • 16×16 viewBox
 *   • stroke 1.5, round line caps + joins
 *   • use currentColor (so any text-* token tints them)
 *
 * Add new icons by exporting another component below. Keep them visually
 * consistent with the existing strokes — no fills.
 */

import { forwardRef, type SVGProps } from "react";

import { cn } from "../../lib/cn";

export interface IconProps extends SVGProps<SVGSVGElement> {
  size?: number;
}

const Base = forwardRef<SVGSVGElement, IconProps & { children: React.ReactNode }>(
  ({ size = 16, className, children, ...rest }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cn("shrink-0", className)}
      {...rest}
    >
      {children}
    </svg>
  ),
);
Base.displayName = "Icon";

export const ChevronLeft = (p: IconProps) => (
  <Base {...p}>
    <path d="m10 12-4-4 4-4" />
  </Base>
);

export const ChevronRight = (p: IconProps) => (
  <Base {...p}>
    <path d="m6 4 4 4-4 4" />
  </Base>
);

export const ChevronDown = (p: IconProps) => (
  <Base {...p}>
    <path d="m4 6 4 4 4-4" />
  </Base>
);

export const X = (p: IconProps) => (
  <Base {...p}>
    <path d="M4 4l8 8M12 4l-8 8" />
  </Base>
);

export const Plus = (p: IconProps) => (
  <Base {...p}>
    <path d="M8 3v10M3 8h10" />
  </Base>
);

export const Search = (p: IconProps) => (
  <Base {...p}>
    <circle cx="7" cy="7" r="4" />
    <path d="m13 13-2.6-2.6" />
  </Base>
);

export const Copy = (p: IconProps) => (
  <Base {...p}>
    <rect x="5" y="5" width="8" height="8" rx="1.5" />
    <path d="M3 11V4a1 1 0 0 1 1-1h7" />
  </Base>
);

export const Check = (p: IconProps) => (
  <Base {...p}>
    <path d="m3.5 8.5 3 3 6-7" />
  </Base>
);

export const Settings = (p: IconProps) => (
  <Base {...p}>
    <circle cx="8" cy="8" r="2" />
    <path d="M8 1.5v1.6M8 12.9v1.6M3.4 3.4l1.13 1.13M11.47 11.47l1.13 1.13M1.5 8h1.6M12.9 8h1.6M3.4 12.6l1.13-1.13M11.47 4.53l1.13-1.13" />
  </Base>
);

export const MoreHorizontal = (p: IconProps) => (
  <Base {...p}>
    <circle cx="3" cy="8" r="1" />
    <circle cx="8" cy="8" r="1" />
    <circle cx="13" cy="8" r="1" />
  </Base>
);

export const Play = (p: IconProps) => (
  <Base {...p}>
    <path d="M5 3.5v9l7-4.5z" />
  </Base>
);

export const Pause = (p: IconProps) => (
  <Base {...p}>
    <path d="M5.5 3v10M10.5 3v10" />
  </Base>
);

export const Inbox = (p: IconProps) => (
  <Base {...p}>
    <rect x="2.5" y="2.5" width="11" height="11" rx="1.5" />
    <path d="M2.5 9.5h3l1 1.5h3l1-1.5h3" />
  </Base>
);

export const CheckCircle = (p: IconProps) => (
  <Base {...p}>
    <circle cx="8" cy="8" r="6" />
    <path d="m5.5 8 2 2 3-4" />
  </Base>
);

export const AlertTriangle = (p: IconProps) => (
  <Base {...p}>
    <path d="M8 2.5 14 13H2zM8 6v3M8 11.5v.01" />
  </Base>
);

export const Send = (p: IconProps) => (
  <Base {...p}>
    <path d="M13.5 2.5 2.5 7l4.5 1.5L8.5 13z" />
    <path d="M7 8.5 13.5 2.5" />
  </Base>
);

export const Sparkles = (p: IconProps) => (
  <Base {...p}>
    <path d="M5 2v2M5 8v2M2 5h2M6 5h2M11 4l1 2 2 1-2 1-1 2-1-2-2-1 2-1z" />
  </Base>
);

export const Slash = (p: IconProps) => (
  <Base {...p}>
    <path d="m11 3-6 10" />
  </Base>
);

export const Cpu = (p: IconProps) => (
  <Base {...p}>
    <rect x="3.5" y="3.5" width="9" height="9" rx="1.5" />
    <rect x="6" y="6" width="4" height="4" />
    <path d="M6 1.5v2M10 1.5v2M6 12.5v2M10 12.5v2M1.5 6h2M1.5 10h2M12.5 6h2M12.5 10h2" />
  </Base>
);

export const Folder = (p: IconProps) => (
  <Base {...p}>
    <path d="M2.5 4a1 1 0 0 1 1-1H7l1.5 1.5h4a1 1 0 0 1 1 1V12a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1z" />
  </Base>
);

export const Trash = (p: IconProps) => (
  <Base {...p}>
    <path d="M3 4h10M6 4V2.5h4V4M5 4l.5 9a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1l.5-9" />
  </Base>
);

export const Brain = (p: IconProps) => (
  <Base {...p}>
    <path d="M6 3a2 2 0 0 0-2 2v1a2 2 0 0 0-1.5 3.5A2 2 0 0 0 4 12.5V13a2 2 0 0 0 2 2" />
    <path d="M10 3a2 2 0 0 1 2 2v1a2 2 0 0 1 1.5 3.5A2 2 0 0 1 12 12.5V13a2 2 0 0 1-2 2" />
    <path d="M8 4v10" />
  </Base>
);

export const ShieldCheck = (p: IconProps) => (
  <Base {...p}>
    <path d="M8 2 3 4v4c0 3 2.2 5.4 5 6 2.8-.6 5-3 5-6V4l-5-2Z" />
    <path d="m6 8 1.5 1.5L10 7" />
  </Base>
);

export const Pencil = (p: IconProps) => (
  <Base {...p}>
    <path d="M11.5 2.5 13.5 4.5 5 13H3v-2L11.5 2.5z" />
  </Base>
);

export const Eye = (p: IconProps) => (
  <Base {...p}>
    <path d="M1.5 8s2.5-4.5 6.5-4.5S14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8z" />
    <circle cx="8" cy="8" r="2" />
  </Base>
);

export const EyeOff = (p: IconProps) => (
  <Base {...p}>
    <path d="M2 2l12 12" />
    <path d="M4 4.5C2.7 5.6 1.5 8 1.5 8s2.5 4.5 6.5 4.5c1.2 0 2.3-.4 3.3-.9" />
    <path d="M7 4a6 6 0 0 1 1 0c4 0 6.5 4 6.5 4s-.6 1.1-1.7 2.3" />
  </Base>
);

export const Layout = (p: IconProps) => (
  <Base {...p}>
    <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
    <path d="M6 2.5v11M2 6.5h4" />
  </Base>
);

export const Terminal = (p: IconProps) => (
  <Base {...p}>
    <rect x="2" y="3" width="12" height="10" rx="1.5" />
    <path d="m4.5 6.5 2 1.5-2 1.5M8.5 10h3" />
  </Base>
);

export const FileText = (p: IconProps) => (
  <Base {...p}>
    <path d="M4 2.5h4L11.5 6v7a.5.5 0 0 1-.5.5H4a.5.5 0 0 1-.5-.5V3a.5.5 0 0 1 .5-.5Z" />
    <path d="M8 2.5V6h3.5" />
    <path d="M5.5 8.5h4M5.5 10.5h4" />
  </Base>
);

export const FilePlus = (p: IconProps) => (
  <Base {...p}>
    <path d="M4 2.5h4L11.5 6v7a.5.5 0 0 1-.5.5H4a.5.5 0 0 1-.5-.5V3a.5.5 0 0 1 .5-.5Z" />
    <path d="M8 2.5V6h3.5" />
    <path d="M7.5 8v3.5M5.75 9.75h3.5" />
  </Base>
);

export const Globe = (p: IconProps) => (
  <Base {...p}>
    <circle cx="8" cy="8" r="5.5" />
    <path d="M2.5 8h11" />
    <path d="M8 2.5c1.7 1.6 2.6 3.5 2.6 5.5S9.7 11.9 8 13.5C6.3 11.9 5.4 10 5.4 8S6.3 4.1 8 2.5Z" />
  </Base>
);

export const ListChecks = (p: IconProps) => (
  <Base {...p}>
    <path d="m2 4.5 1 1L5 3.5M2 10.5l1 1L5 9.5" />
    <path d="M7.5 5h6.5M7.5 11h6.5" />
  </Base>
);

export const Maestro = ({ size = 16, className, ...rest }: IconProps) => (
  // Three offset bars suggesting an orchestra/conductor — Maestro sigil
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    focusable="false"
    className={cn("shrink-0", className)}
    {...rest}
  >
    <rect x="2" y="3" width="2" height="10" rx="1" fill="currentColor" />
    <rect x="7" y="5" width="2" height="8" rx="1" fill="currentColor" opacity="0.8" />
    <rect x="12" y="7" width="2" height="6" rx="1" fill="currentColor" opacity="0.55" />
  </svg>
);
